import { useContext, useEffect, useState } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import Table from 'react-bootstrap/Table';
import OperatorsTableHeadRow from './components/OperatorsTableHeadRow';
import { OperatorsTableRowContent } from './components/OperatorsTableRowContent';
import PopUpManageOperatorScheme from './components/PopUpManageOperatorScheme';
import Loader from 'components/Loader';
import i18nContext from 'components/i18n-context';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import Button from 'uikit/Button/Button';
import { Container } from 'uikit/Container/Container';
import { PopUp } from 'uikit/PopUp/PopUp';
import './OperatorsPage.scss';

const popUpInitialData = {
  isShowPopUp: false,
  operatorData: null,
  onCancel: () => {},
  onConfirm: () => {}
};

const OperatorsPage = ({ operatorsStore }) => {
  const i18n = useContext(i18nContext);
  const [popUpData, setPopUpData] = useState(popUpInitialData);

  useEffect(() => {
    if (!operatorsStore.isInitialized) {
      operatorsStore.getOperatorsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorsStore.isInitialized]);

  const resetPopUpData = () => {
    setPopUpData(popUpInitialData);
  };

  const handleCreateOperator = () => {
    setPopUpData({
      isShowPopUp: true,
      operatorData: null,
      onConfirm: async (data) => {
        try {
          await operatorsStore.createOperator(data);
          await operatorsStore.getOperatorsList(); // Refresh the list
          resetPopUpData();
        } catch (error) {
          // Error is handled by the store with toast notifications
          // eslint-disable-next-line no-console
          console.error('Failed to create operator:', error);
        }
      },
      onCancel: resetPopUpData
    });
  };

  const handleUpdateOperator = (operator) => {
    setPopUpData({
      isShowPopUp: true,
      operatorData: operator,
      onConfirm: async (data) => {
        try {
          await operatorsStore.updateOperator(data);
          await operatorsStore.getOperatorsList(); // Refresh the list
          resetPopUpData();
        } catch (error) {
          // Error is handled by the store with toast notifications
          // eslint-disable-next-line no-console
          console.error('Failed to update operator:', error);
        }
      },
      onCancel: resetPopUpData
    });
  };

  if (!operatorsStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  // TODO: Uncomment when role checking is implemented
  // const canManageOperators = hasRole('SUPER_ADMIN');

  return (
    <Container className={'operators-container'} header={i18n.getMessage('operators.header')}>
      <div className={'operators-actions-wrapper'}>
        <Button
          className={'operators-button'}
          size={'medium'}
          type={'primary'}
          onClick={handleCreateOperator}
          // isDisabled={!canManageOperators}
        >
          {i18n.getMessage('operators.button.createOperator')}
        </Button>
      </div>

      {operatorsStore.operatorsList?.length === 0 ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('operators.table.emptyPlug.header')}
          descriptionText={i18n.getMessage('operators.table.emptyPlug.description')}
        />
      ) : (
        <Table responsive>
          <thead>
            <OperatorsTableHeadRow handleOnSortTable={() => {}} sortBy={null} direction={null} />
          </thead>
          <tbody>
            {operatorsStore?.operatorsList?.map((operator, index) => (
              <OperatorsTableRowContent
                key={operator.id || index}
                operatorData={operator}
                handleUpdateOperator={handleUpdateOperator}
                isLoading={operatorsStore.isLoading}
              />
            ))}
          </tbody>
        </Table>
      )}

      <PopUp alignOnCenter show={popUpData.isShowPopUp}>
        <PopUpManageOperatorScheme
          isLoading={operatorsStore.isLoading}
          operatorData={popUpData.operatorData}
          handleClose={popUpData.onCancel}
          handleSave={popUpData.onConfirm}
        />
      </PopUp>
    </Container>
  );
};

OperatorsPage.propTypes = {
  operatorsStore: MobXPropTypes.observableObject
};

export default inject('operatorsStore')(observer(OperatorsPage));
